import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import PermissoesCaixa from "../features/caixa/components/PermissoesCaixa";
import { CHAVES_CAIXA } from "../features/caixa/lib/permissoes";
import { PermissoesModulos, ACOES_PEDIDO, CHAVES_MODULOS, presetsPorCargo } from "../features/funcionarios";
import { PermissaoReabrir, PERMISSAO_REABRIR } from "../features/pedidos-reabrir";
import { temAcessoTotal, temPermissao, usuarioLogado } from "../lib/permissoes";
import { toast } from "sonner";
import {
  Search, Plus, User, Phone, Mail, Trash2,
  Edit, X, Bike, ChefHat, UserCog, Users, KeyRound, Lock, ShieldCheck
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { CarregandoGrade } from "../components/carregando";

const API = '/api';

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// Cargos disponíveis (precisa bater com CARGO_PRESETS no backend)
// `plural` é o título do grupo na lista: colar um "s" no label dava
// "Entregadors" (achado 337).
const CARGOS = [
  { value: "entregador", label: "Entregador", plural: "Entregadores", icon: Bike, color: "text-purple-500 bg-purple-500/10" },
  { value: "cozinheiro", label: "Cozinheiro", plural: "Cozinheiros", icon: ChefHat, color: "text-orange-500 bg-orange-500/10" },
  { value: "atendente", label: "Atendente", plural: "Atendentes", icon: User, color: "text-blue-500 bg-blue-500/10" },
  { value: "gerente", label: "Gerente", plural: "Gerentes", icon: UserCog, color: "text-green-500 bg-green-500/10" },
];

// Cargos prontos e a lista de tudo o que dá para marcar (módulos + ações).
// O catálogo mora em features/funcionarios e precisa bater com o backend
// (api/v1/auth/permissions.py).
const CARGO_PRESETS = presetsPorCargo(CHAVES_CAIXA);
const TODAS_AS_PERMISSOES = [...CHAVES_MODULOS, ...CHAVES_CAIXA, PERMISSAO_REABRIR, ...ACOES_PEDIDO.map((a) => a.id)];

// Funcionário com "Pessoas" só libera o que ele mesmo tem (o servidor recusa o
// resto). Para ele, o cargo pré-marca só a parte que ele pode dar.
const podeConceder = (chave) => temPermissao(usuarioLogado(), chave);
const presetDoCargo = (cargo) => (CARGO_PRESETS[cargo] || []).filter(podeConceder);

export default function Funcionarios() {
  const [funcionarios, setFuncionarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clientesProntos, setClientesProntos] = useState(false);
  const [primeiraCarga, setPrimeiraCarga] = useState(true); // esqueleto só na primeira abertura
  const [searchTerm, setSearchTerm] = useState("");

  // Modal único de cadastro/edição
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create" | "edit"
  const [funcionarioEditando, setFuncionarioEditando] = useState(null);

  const [clienteSearch, setClienteSearch] = useState("");
  const [clientesSugeridos, setClientesSugeridos] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [cargoSelecionado, setCargoSelecionado] = useState("");
  const [senha, setSenha] = useState("");
  const [allowedModules, setAllowedModules] = useState([]);
  // Se true, sobreescreve o preset ao mudar cargo. Vira true quando o user mexe nos toggles.
  const [customPermissions, setCustomPermissions] = useState(false);
  const [saving, setSaving] = useState(false);

  // A equipe e a lista de clientes vêm separadas (25/10/2026): a de clientes é
  // pesada (loja grande leva segundos) e só serve para as sugestões do cadastro —
  // antes a tela inteira esperava por ela num spinner.
  const fetchData = useCallback(async () => {
    try {
      const funcRes = await axios.get(`${API}/funcionarios`, getAuthHeader());
      setFuncionarios(funcRes.data);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar funcionários");
    } finally {
      setPrimeiraCarga(false);
    }
  }, []);

  const fetchClientes = useCallback(async () => {
    try {
      const clientesRes = await axios.get(`${API}/clientes`, getAuthHeader());
      setClientes(clientesRes.data);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
    } finally {
      setClientesProntos(true);
    }
  }, []);

  useEffect(() => { fetchData(); fetchClientes(); }, [fetchData, fetchClientes]);

  // Sugestões de cliente
  useEffect(() => {
    if (clienteSearch.length < 2 || modalMode === "edit") {
      setClientesSugeridos([]);
      return;
    }
    const term = clienteSearch.toLowerCase();
    const usedIds = new Set(funcionarios.map(f => f.cliente_id));
    setClientesSugeridos(
      clientes
        .filter(c => !usedIds.has(c.id))
        .filter(c =>
          c.nome?.toLowerCase().includes(term) ||
          c.telefone?.includes(term) ||
          c.email?.toLowerCase().includes(term)
        )
        .slice(0, 5)
    );
  }, [clienteSearch, clientes, funcionarios, modalMode]);

  // Quando muda o cargo, aplica preset (a menos que o usuário já tenha customizado)
  useEffect(() => {
    if (!cargoSelecionado) return;
    if (customPermissions) return;
    setAllowedModules(presetDoCargo(cargoSelecionado));
  }, [cargoSelecionado, customPermissions]);

  const resetForm = () => {
    setClienteSearch("");
    setClienteSelecionado(null);
    setCargoSelecionado("");
    setSenha("");
    setAllowedModules([]);
    setCustomPermissions(false);
    setClientesSugeridos([]);
    setFuncionarioEditando(null);
  };

  const handleAbrirCriacao = () => {
    resetForm();
    setModalMode("create");
    setModalOpen(true);
  };

  const handleAbrirEdicao = (funcionario) => {
    resetForm();
    setModalMode("edit");
    setFuncionarioEditando(funcionario);
    setCargoSelecionado(funcionario.cargo?.toLowerCase() || "");
    // Lista vazia = dono desmarcou tudo; preset só quando nunca foi gravada.
    const am = Array.isArray(funcionario.allowed_modules)
      ? funcionario.allowed_modules
      : presetDoCargo(funcionario.cargo?.toLowerCase());
    setAllowedModules(am);
    // Considera customizado se diverge do preset do cargo
    const preset = presetDoCargo(funcionario.cargo?.toLowerCase());
    setCustomPermissions(JSON.stringify([...preset].sort()) !== JSON.stringify([...am].sort()));
    setModalOpen(true);
  };

  const toggleModule = (id) => {
    setCustomPermissions(true);
    setAllowedModules(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const restorePreset = () => {
    setCustomPermissions(false);
    setAllowedModules(presetDoCargo(cargoSelecionado));
  };

  const tudoMarcado = TODAS_AS_PERMISSOES.every((c) => allowedModules.includes(c));
  const marcarTudo = () => {
    setCustomPermissions(true);
    setAllowedModules((prev) => (tudoMarcado ? [] : [...new Set([...prev, ...TODAS_AS_PERMISSOES.filter(podeConceder)])]));
  };

  const handleSelecionarCliente = (cliente) => {
    setClienteSelecionado(cliente);
    setClienteSearch(cliente.nome);
    setClientesSugeridos([]);
  };

  const handleSubmit = async () => {
    // Validações
    if (modalMode === "create" && !clienteSelecionado) {
      toast.error("Selecione um cliente");
      return;
    }
    if (!cargoSelecionado) {
      toast.error("Selecione um cargo");
      return;
    }
    if (senha) {
      if (senha.length < 4) {
        toast.error("Senha deve ter no mínimo 4 dígitos");
        return;
      }
      if (!/^\d+$/.test(senha)) {
        toast.error("Senha deve conter apenas números (PIN)");
        return;
      }
    }
    if (modalMode === "create" && !senha) {
      // Em criação, sem senha = funcionário sem acesso ao sistema. Avisamos.
      const ok = window.confirm(
        "Sem senha, este funcionário não conseguirá fazer login no sistema. Deseja continuar mesmo assim?"
      );
      if (!ok) return;
    }

    setSaving(true);
    try {
      if (modalMode === "create") {
        const payload = {
          cliente_id: clienteSelecionado.id,
          cargo: cargoSelecionado,
        };
        if (senha) payload.senha = senha;
        // Sem lista, o servidor aplica o preset inteiro do cargo — que o funcionário
        // que está cadastrando talvez não possa dar. Dele vai sempre a lista da tela.
        if (customPermissions || !temAcessoTotal(usuarioLogado())) payload.allowed_modules = allowedModules;
        await axios.post(`${API}/funcionarios`, payload, getAuthHeader());
        toast.success("Funcionário cadastrado!");
      } else {
        const payload = {
          cargo: cargoSelecionado,
          allowed_modules: allowedModules,
        };
        if (senha) payload.senha = senha;
        await axios.put(`${API}/funcionarios/${funcionarioEditando.id}`, payload, getAuthHeader());
        toast.success("Funcionário atualizado!");
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar funcionário");
    } finally {
      setSaving(false);
    }
  };

  const handleRemover = async (funcionario) => {
    if (!window.confirm(`Deseja remover ${funcionario.nome} da equipe?`)) return;
    try {
      await axios.delete(`${API}/funcionarios/${funcionario.id}`, getAuthHeader());
      toast.success("Funcionário removido!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao remover funcionário");
    }
  };

  const getCargoInfo = (cargo) =>
    CARGOS.find(c => c.value === cargo?.toLowerCase()) || CARGOS[0];

  const funcionariosFiltrados = funcionarios.filter(f => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      f.nome?.toLowerCase().includes(term) ||
      f.cargo?.toLowerCase().includes(term) ||
      f.telefone?.includes(term)
    );
  });

  const funcionariosPorCargo = CARGOS.map(cargo => ({
    ...cargo,
    funcionarios: funcionariosFiltrados.filter(f => f.cargo?.toLowerCase() === cargo.value)
  })).filter(g => g.funcionarios.length > 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-7 h-7 text-primary" />
            Funcionários
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Cadastre acessos personalizados pra equipe — cada um vê só os módulos que precisa.
          </p>
        </div>
        <Button onClick={handleAbrirCriacao} size="lg" className="gap-2">
          <Plus className="w-5 h-5" />
          Cadastrar Funcionário
        </Button>
      </div>

      {/* Busca */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          placeholder="Buscar funcionário por nome, cargo ou telefone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Lista — na primeira abertura, cards piscando no formato dos cartões da equipe */}
      {primeiraCarga ? (
        <CarregandoGrade quantos={6} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" altura="h-[150px]" />
      ) : funcionariosFiltrados.length === 0 ? (
        <div className="text-center py-16 bg-muted/30 rounded-2xl border-2 border-dashed">
          <Users className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Nenhum funcionário cadastrado</h3>
          <p className="text-muted-foreground mb-4">
            Cadastre funcionários a partir dos clientes existentes
          </p>
          <Button onClick={handleAbrirCriacao} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Cadastrar primeiro funcionário
          </Button>
        </div>
      ) : (
        <div className="space-y-8">
          {funcionariosPorCargo.map(grupo => {
            const IconComponent = grupo.icon;
            return (
              <div key={grupo.value}>
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${grupo.color}`}>
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <h2 className="font-bold text-lg">{grupo.plural}</h2>
                  <span className="bg-muted px-2 py-0.5 rounded-full text-xs font-medium">
                    {grupo.funcionarios.length}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {grupo.funcionarios.map(funcionario => {
                    const cargoInfo = getCargoInfo(funcionario.cargo);
                    const CargoIcon = cargoInfo.icon;
                    const hasLogin = !!funcionario.has_password;

                    return (
                      <div
                        key={funcionario.id}
                        className="bg-card rounded-xl border p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center overflow-hidden shrink-0">
                            {funcionario.foto ? (
                              <img src={funcionario.foto} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <User className="w-6 h-6 text-muted-foreground" />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold truncate">{funcionario.nome}</h3>
                            <div className="flex items-center gap-1.5 flex-wrap mt-1">
                              <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cargoInfo.color}`}>
                                <CargoIcon className="w-3 h-3" />
                                {cargoInfo.label}
                              </div>
                              {hasLogin ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
                                  <ShieldCheck className="w-3 h-3" />
                                  acesso ativo
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground">
                                  <Lock className="w-3 h-3" />
                                  sem login
                                </span>
                              )}
                            </div>

                            {funcionario.telefone && (
                              <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {funcionario.telefone}
                              </p>
                            )}
                            {funcionario.email && (
                              <p className="text-sm text-muted-foreground flex items-center gap-1 truncate">
                                <Mail className="w-3 h-3" />
                                {funcionario.email}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex gap-2 mt-4 pt-3 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => handleAbrirEdicao(funcionario)}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleRemover(funcionario)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal único de criação/edição */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {modalMode === "create" ? (
                <><Plus className="w-5 h-5 text-primary" /> Cadastrar Funcionário</>
              ) : (
                <><Edit className="w-5 h-5 text-primary" /> Editar Funcionário</>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* Cliente — só em CREATE */}
            {modalMode === "create" && (
              <div>
                <Label>Buscar Cliente *</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Digite nome, telefone ou email..."
                    value={clienteSearch}
                    onChange={(e) => {
                      setClienteSearch(e.target.value);
                      setClienteSelecionado(null);
                    }}
                    className="pl-9"
                  />
                  {clientesSugeridos.length > 0 && !clienteSelecionado && (
                    <div className="absolute z-10 w-full mt-1 bg-popover border rounded-lg shadow-lg max-h-48 overflow-auto">
                      {clientesSugeridos.map(cliente => (
                        <button
                          key={cliente.id}
                          onClick={() => handleSelecionarCliente(cliente)}
                          className="w-full px-3 py-2 text-left hover:bg-muted flex items-center gap-3"
                        >
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                            {cliente.foto ? (
                              <img src={cliente.foto} alt="" className="w-full h-full object-cover rounded-full" />
                            ) : (
                              <User className="w-4 h-4 text-muted-foreground" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{cliente.nome}</p>
                            <p className="text-xs text-muted-foreground">{cliente.telefone || cliente.email}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {!clientesProntos && clienteSearch.length >= 2 && !clienteSelecionado && (
                  <p className="text-xs text-muted-foreground mt-1">Carregando a lista de clientes…</p>
                )}
                {clienteSelecionado && (
                  <div className="mt-2 p-3 bg-muted/50 rounded-lg flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      {clienteSelecionado.foto ? (
                        <img src={clienteSelecionado.foto} alt="" className="w-full h-full object-cover rounded-full" />
                      ) : (
                        <User className="w-5 h-5 text-primary" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{clienteSelecionado.nome}</p>
                      <p className="text-xs text-muted-foreground">{clienteSelecionado.telefone}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => { setClienteSelecionado(null); setClienteSearch(""); }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Info do funcionário em EDIT */}
            {modalMode === "edit" && funcionarioEditando && (
              <div className="p-3 bg-muted/50 rounded-lg flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  {funcionarioEditando.foto ? (
                    <img src={funcionarioEditando.foto} alt="" className="w-full h-full object-cover rounded-full" />
                  ) : (
                    <User className="w-5 h-5 text-primary" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-medium">{funcionarioEditando.nome}</p>
                  <p className="text-xs text-muted-foreground">{funcionarioEditando.telefone}</p>
                </div>
              </div>
            )}

            {/* Cargo */}
            <div>
              <Label>Cargo *</Label>
              <Select value={cargoSelecionado} onValueChange={(v) => { setCargoSelecionado(v); setCustomPermissions(false); }}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione o cargo..." />
                </SelectTrigger>
                <SelectContent>
                  {CARGOS.map(cargo => {
                    const IconComponent = cargo.icon;
                    return (
                      <SelectItem key={cargo.value} value={cargo.value}>
                        <div className="flex items-center gap-2">
                          <IconComponent className="w-4 h-4" />
                          {cargo.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {cargoSelecionado === "entregador" && (
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <Bike className="w-3 h-3" />
                  Aparecerá na aba Delivery como entregador
                </p>
              )}
            </div>

            {/* Senha PIN */}
            <div>
              <Label className="flex items-center gap-2">
                <KeyRound className="w-4 h-4" />
                Senha de acesso
                {modalMode === "edit" && funcionarioEditando?.has_password && (
                  <span className="text-[10px] text-muted-foreground font-normal">(deixe em branco para manter a atual)</span>
                )}
              </Label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
                placeholder={modalMode === "edit" && funcionarioEditando?.has_password ? "Nova senha (opcional)" : "PIN de 4 a 10 dígitos"}
                value={senha}
                onChange={(e) => setSenha(e.target.value.replace(/\D/g, ""))}
                className="mt-1 font-mono tracking-widest"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                O funcionário usará o <strong>telefone</strong> {modalMode === "create" && clienteSelecionado ? <span className="font-mono">({clienteSelecionado.telefone})</span> : "do cadastro"} + essa senha para entrar.
              </p>
            </div>

            {/* Permissões */}
            {cargoSelecionado && (
              <PermissoesModulos
                selecionadas={allowedModules}
                onToggle={toggleModule}
                resumo={customPermissions ? "(customizado)" : `(preset do cargo ${cargoSelecionado})`}
                podeConceder={podeConceder}
                tudoMarcado={tudoMarcado}
                onMarcarTudo={marcarTudo}
                onRestaurar={customPermissions ? restorePreset : undefined}
              />
            )}

            {cargoSelecionado && <PermissoesCaixa selecionadas={allowedModules} onToggle={toggleModule} />}
            {cargoSelecionado && <PermissaoReabrir selecionadas={allowedModules} onToggle={toggleModule} extras={ACOES_PEDIDO} />}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setModalOpen(false)}>
                Cancelar
              </Button>
              <Button className="flex-1" onClick={handleSubmit} disabled={saving}>
                {saving ? "Salvando..." : (modalMode === "create" ? "Cadastrar" : "Salvar")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
